import {
  CircleCheck,
  Info,
  LoaderCircle,
  OctagonX,
  TriangleAlert
} from "lucide-react";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

// The shadcn CLI's default template drives theme via next-themes' useTheme()
// - this app isn't Next.js and already tracks dark/light itself
// (lib/preferences.tsx's resolved `dark` + `data-theme` attribute), so next-themes
// would silently always report "system" here. Takes an explicit `dark` prop
// from the caller instead; the next-themes dependency this CLI add pulled in
// is unused and can be removed.
const Toaster = ({ dark, ...props }: ToasterProps & { dark: boolean }) => {
  return (
    <Sonner
      theme={dark ? "dark" : "light"}
      position="top-right"
      className="toaster group"
      // Sonner injects its own <style> into <head> at mount time (after
      // Tailwind's compiled CSS, which is already in the document from
      // first paint) and that stylesheet hardcodes font-family directly on
      // [data-sonner-toaster] - at equal selector specificity, a LATER
      // stylesheet rule wins, so Tailwind's font-sans utility class loses
      // this particular tie no matter which font it's configured with. An
      // inline style always wins over any stylesheet rule regardless of
      // injection order, so that's what actually overrides it here.
      style={{ fontFamily: "inherit" }}
      icons={{
        success: <CircleCheck className="h-4 w-4" />,
        info: <Info className="h-4 w-4" />,
        warning: <TriangleAlert className="h-4 w-4" />,
        error: <OctagonX className="h-4 w-4" />,
        loading: <LoaderCircle className="h-4 w-4 animate-spin" />
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          // muted-foreground reads fine on the default bg-background toast,
          // but would be illegible on the error variant's solid
          // bg-destructive below - Sonner tags each toast with
          // data-type="error", which this targets to keep a description (if
          // a future toast.error(msg, {description}) call ever adds one)
          // readable against that solid red instead of muddy grey-on-red.
          description:
            "group-[.toast]:text-muted-foreground group-data-[type=error]:!text-destructive-foreground/90",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          // toast.error(...) (validation failures, stale-decision 409s, every
          // other error toast in the app) as a SOLID destructive-red toast -
          // bg-destructive/text-destructive-foreground, the exact pairing
          // StatusBadge already uses for its own "rejected" tone, not the
          // faint tinted-background treatment this app's inline warning
          // banners use (bg-destructive/10 etc.) - those sit inside a card
          // that already has a neutral background behind them for contrast;
          // a floating toast has no such surrounding context and needs to
          // read as an unmistakable error at a glance, often while the user
          // isn't even looking at this corner of the screen.
          error:
            "group-[.toaster]:!bg-destructive group-[.toaster]:!text-destructive-foreground group-[.toaster]:!border-destructive"
        }
      }}
      {...props}
    />
  );
};

export { Toaster };
